
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette, faCheck, faDroplet } from '@fortawesome/free-solid-svg-icons';
import { ChromePicker } from 'react-color'; // Need to install react-color
import styles from './EPKBuilder.module.css';
const ThemeStep = ({ data, updateData, errors, mode }) => {
// Initialize with data from props
const [theme, setTheme] = useState({
template: data.theme?.template || 'minimal',
accentColor: data.theme?.accentColor || '#E2384D',
backgroundColor: data.theme?.backgroundColor || '#FFFFFF'
});
// State for color picker
const [showColorPicker, setShowColorPicker] = useState(false);
const [activeColor, setActiveColor] = useState('accent'); // 'accent' or 'background'
// Use ref to prevent update loops
const initialRender = useRef(true);
const prevThemeRef = useRef(theme);
// Sync with parent component only when data actually changes
useEffect(() => {
// Skip the update on the initial render
if (initialRender.current) {
initialRender.current = false;
prevThemeRef.current = theme;
return;
}
// Only update if data has actually changed
if (JSON.stringify(prevThemeRef.current) !== JSON.stringify(theme)) {
  console.log('ThemeStep: Syncing with parent component', theme);
  updateData(theme);
  
  // Update ref
  prevThemeRef.current = theme;
}
}, [theme, updateData]);
// Available templates
const templates = [
{
id: 'minimal',
name: 'Minimal',
description: 'Clean, simple layout focused on content with minimal styling'
},
{
id: 'visual',
name: 'Visual',
description: 'Bold, image-focused layout with more visual elements'
},
{
id: 'classic',
name: 'Classic',
description: 'Traditional press kit style with a formal, structured layout'
}
];
// Available color options - predefined presets
const colorOptions = [
{ id: 'red', value: '#E2384D' },
{ id: 'blue', value: '#2196F3' },
{ id: 'green', value: '#4CAF50' },
{ id: 'purple', value: '#9C27B0' },
{ id: 'orange', value: '#FF9800' },
{ id: 'teal', value: '#009688' }
];
// Background color options - predefined presets
const backgroundColorOptions = [
{ id: 'white', value: '#FFFFFF' },
{ id: 'light', value: '#F5F5F5' },
{ id: 'dark', value: '#1F2937' },
{ id: 'black', value: '#000000' },
];
// Handle template selection
const handleTemplateSelect = (templateId) => {
if (mode === 'view') return;
setTheme(prev => ({
  ...prev,
  template: templateId
}));
};
// Handle color selection
const handleColorSelect = (colorType, colorValue) => {
if (mode === 'view') return;
setTheme(prev => ({
  ...prev,
  [colorType]: colorValue
}));
};
// Handle custom color change from color picker
const handleColorPickerChange = (color) => {
if (mode === 'view') return;
setTheme(prev => ({
  ...prev,
  [activeColor === 'accent' ? 'accentColor' : 'backgroundColor']: color.hex
}));
};

// Render color picker section
const renderColorPicker = () => (
<div className={styles.colorPickerContainer}>
<div className={styles.colorPickerTabs}>
<button
className={`${styles.colorPickerTab} ${activeColor === 'accent' ? styles.activeTab : ''}}
onClick={() => setActiveColor('accent')`}
>
Accent Color
</button>
<button
className={`${styles.colorPickerTab} ${activeColor === 'background' ? styles.activeTab : ''}}
onClick={() => setActiveColor('background')`}
>
Background Color
</button>
</div>
  <ChromePicker 
    color={activeColor === 'accent' ? theme.accentColor : theme.backgroundColor}
    onChange={handleColorPickerChange}
    disableAlpha={true}
  />
  
  <button 
    className={styles.closePickerButton}
    onClick={() => setShowColorPicker(false)}
  >
    Done
  </button>
</div>
);

return (
<div className={`${styles.formSection} ${mode === 'view' ? styles.viewMode : ''}`}>
<h2 className={styles.sectionTitle}>Theme & Layout</h2>
<p className={styles.sectionDescription}>
Customize the look and feel of your EPK by choosing a layout template and colors.
</p>
  {/* Template Selection */}
  <div className={styles.themeSection}>
    <h3>Choose a Layout Template</h3>
    
    <div className={styles.templateGrid}>
      {templates.map(template => (
        <div
          key={template.id}
          className={`${styles.templateItem} ${theme.template === template.id ? styles.active : ''}`}
          onClick={() => handleTemplateSelect(template.id)}
        >
          <div className={styles.templatePreview}>
            {/* Template preview thumbnail */}
            <div
              className={styles.templateThumbnail}
              style={{
                backgroundImage: `url(/images/epk-templates/${template.id}.jpg)`,
                backgroundColor: theme.template === template.id ? 'rgba(226, 56, 77, 0.05)' : 'transparent'
              }}
            >
              {theme.template === template.id && (
                <div className={styles.templateSelected}>
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              )}
            </div>
          </div>
          <div className={styles.templateInfo}>
            <h4 className={styles.templateName}>{template.name}</h4>
            <p className={styles.templateDesc}>{template.description}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
  
  {/* Color Selection */}
  <div className={styles.themeSection}>
    <h3>Choose Colors</h3>
    
    <div className={styles.colorSelectionContainer}>
      <div className={styles.colorCategory}>
        <h4>Accent Color</h4>
        <div className={styles.colorOptions}>
          {colorOptions.map(color => (
            <div
              key={color.id}
              className={`${styles.colorOption} ${theme.accentColor === color.value ? styles.active : ''}`}
              style={{ backgroundColor: color.value }}
              onClick={() => handleColorSelect('accentColor', color.value)}
              title={color.id.charAt(0).toUpperCase() + color.id.slice(1)}
            >
              {theme.accentColor === color.value && (
                <FontAwesomeIcon icon={faCheck} className={styles.colorCheck} />
              )}
            </div>
          ))}
        </div>
      </div>
      
      <div className={styles.colorCategory}>
        <h4>Background Color</h4>
        <div className={styles.colorOptions}>
          {backgroundColorOptions.map(color => (
            <div
              key={color.id}
              className={`${styles.colorOption} ${theme.backgroundColor === color.value ? styles.active : ''}`}
              style={{ backgroundColor: color.value }}
              onClick={() => handleColorSelect('backgroundColor', color.value)}
              title={color.id.charAt(0).toUpperCase() + color.id.slice(1)}
            >
              {theme.backgroundColor === color.value && (
                <FontAwesomeIcon icon={faCheck} className={styles.colorCheck} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
    
    <button 
      className={styles.customColorButton}
      onClick={() => setShowColorPicker(!showColorPicker)}
    >
      <FontAwesomeIcon icon={faDroplet} />
      Custom Color Picker
    </button>
    
    {showColorPicker && renderColorPicker()}
  </div>
  
  {/* Preview hint */}
  <div className={styles.previewHint}>
    <FontAwesomeIcon icon={faPalette} className={styles.hintIcon} />
    <p>
      Your theme selection will be applied to your EPK. You'll see a preview of your complete EPK in the next step.
    </p>
  </div>
</div>
);
};
export default ThemeStep;